import { getJwtTokenCustomerListApi } from "@/api/customer.js";
import { getSettingApi, getUserInfoV2Api } from "@/api/login/utility";
import { multiLogin } from "@/api/va-order-page/orderTest";
import { getSettingData } from "@/api/setting";
import { getRedirectUrl } from "@/utils";

// Optional Sentry import
let Sentry;
try {
  Sentry = require("@sentry/vue");
} catch (e) {
  console.log("Sentry not available");
}

const user = {
  namespaced: true,
  state: {
    customerList: [],
    customer: null,
    userInfo: null,
    patientId: null,
    patientCountry: "", // two digits country abbr, such as "US",
    updatePatientCount: 0,
    updateFromExpressCheckoutPPNDialog: 0,
    closeExpressCheckoutPPNDialog: 0,
    isUpdatingPatient: false,
  },
  getters: {
    isPatientPhoneMissing: (state, getters, rootState, rootGetters) => {
      const patient = rootState.orderTest.order.patient || {};
      const isPhoneNumberMissing = !patient.phone || patient.phone.trim() === "";
      const patientCountry = patient.country;
      const isCountryInternational = patientCountry && patientCountry !== "US";
      return isCountryInternational && isPhoneNumberMissing;
    }
  },
  mutations: {
    SET_CUSTOMER_LIST(state, customerList) {
      state.customerList = customerList;
    },

    SET_CUSTOMER(state, customer) {
      state.customer = customer;
    },

    SET_USER_INFO(state, userInfo) {
      state.userInfo = userInfo;
    },

    SET_PATIENT_COUNTRY(state, country) {
      state.patientCountry = country;
    },    

    SET_UPDATE_PATIENT_COUNT(state) {
      state.updatePatientCount ++
    },
    SET_UPDATE_FROM_EXPRESS_CHECKOUT_PPN_DIALOG(state) {
      state.updateFromExpressCheckoutPPNDialog++
    },
    CLOSE_EXPRESS_CHECKOUT_PPN_DIALOG(state) {
      state.closeExpressCheckoutPPNDialog++
    },
    SET_IS_UPDATING_PATIENT(state, isUpdating) {
      state.isUpdatingPatient = isUpdating;
    },
  },
  actions: {
    async setCustomerList({ state, commit }) {
      if(state.userInfo) {
        let { isClinic } = state.userInfo;
        if (isClinic) {
          const { data } = await getJwtTokenCustomerListApi();
          let customerListFromApi = data;
          let modifiedCustomerList = [];
          customerListFromApi.forEach(customer => {
            let id = Object.keys(customer)[0];
            let name = customer[id];
            // hide closed customer account - name includs "(c)" or "(C)"
            if (!name.toLowerCase().includes("(c)")) {
              modifiedCustomerList.push({ id, name, role: "provider" });
            }
          });
          commit("SET_CUSTOMER_LIST", modifiedCustomerList);
        }  
      }
    },
    async setDefaultCustomer({ state, commit, dispatch }) {
      if (!state.userInfo) {
        return;
      }
      let { isClinic, customer_id } = state.userInfo;
      if (!isClinic) {
        return;
      }
      try {
        // get default customer id
        let practiceInfoResp = await getSettingData("/setting/getPracticeInfo");
        let defaultCustomerId = practiceInfoResp.data.default_customer;
        if (["null", "undefined"].includes(defaultCustomerId) || !defaultCustomerId) {
          // if defaultCustomerId is not set, then let jwt customer be the default one
          defaultCustomerId = customer_id;
        }
        if (customer_id.toString() !== defaultCustomerId.toString()) {
          // check if current user is not the same as default customer
          // get default customer's token & update userInfo
          try {
            let multiLoginResp = await multiLogin({ customerId: defaultCustomerId });
            sessionStorage.removeItem("jwtToken");
            sessionStorage.setItem("jwtToken", multiLoginResp.data);
            await dispatch("setUserInfo");
          } catch(err) {
            console.log("multiLogin error", err);
            localStorage.removeItem("jwtToken");
            sessionStorage.removeItem("jwtToken");
            let logOutUrl = getRedirectUrl("loginPage");
            window.location.href = logOutUrl;
          }
        }
        // set default customer
        let defaultCustomer = state.customerList
          .find(customer => customer.id === defaultCustomerId.toString());
        if (defaultCustomer) {
          commit("SET_CUSTOMER", defaultCustomer);
        }
      } catch(err) {
        console.log("get default customer error", err);
      }
    },

    setCustomer({ commit }, customer) {
      commit("SET_CUSTOMER", customer);
    },

    setUpdatePatientCount({ commit }) {
      commit("SET_UPDATE_PATIENT_COUNT");
    },
    setUpdateFromExpressCheckoutPPNDialog({ commit }) {
      commit("SET_UPDATE_FROM_EXPRESS_CHECKOUT_PPN_DIALOG");
    },
    closeExpressCheckoutPPNDialog({ commit }) {
      commit("CLOSE_EXPRESS_CHECKOUT_PPN_DIALOG");
    },

    async setUserInfo({ commit, rootGetters }) {
      let jwtToken = sessionStorage.getItem("jwtToken");
      if (!jwtToken) {
        const fallbackToken =
          rootGetters["login/token"] ||
          sessionStorage.getItem("token") ||
          localStorage.getItem("token");
        if (fallbackToken) {
          sessionStorage.setItem("jwtToken", fallbackToken);
          jwtToken = fallbackToken;
        }
      }
      if (!jwtToken) {
        return;
      }
      const { data } = await getUserInfoV2Api();
      let settingResp = await getSettingApi();
      let userInfo = data.result;
      if (settingResp.data) {
        userInfo.joinBetaProgram = settingResp.data.joinBetaProgram === "0" ? false : true;
      }
      let { user_roles, customer_id, clinic_id } = userInfo;
      userInfo.isClinic = user_roles.includes("clinic");
      // set Sentry scope
      if (Sentry && Sentry.configureScope) {
        Sentry.configureScope(function(scope) {
          scope.setTag( "user-role", userInfo.isClinic ? "clinic" : "customer");
          scope.setUser({ id: customer_id });
        });
      }
      commit("SET_USER_INFO", userInfo);
    },

    setPatientCountry({ commit }, country) {
      commit("SET_PATIENT_COUNTRY", country);
    },

    setUpdatingPatient({ commit }, isUpdating) {
      commit("SET_IS_UPDATING_PATIENT", isUpdating);
    },
  }
};

export default user;